import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  PageHeader, KPICard, KPIGrid, EmptyState, FilterBar, StatusBadge, SectionCard, PillTabs,
} from "@/components/app/PageKit";
import {
  UserPlus, UserMinus, FileText, Package, Calendar, ArrowRight,
  Plus, MoreHorizontal, Bell, ChevronRight, MapPin, Briefcase,
  ClipboardList, CheckCircle2, AlertCircle,
} from "lucide-react";

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

/* ============== JOURNEY CARD ============== */
function JourneyCard({ kind, item, onView, onContinue, onRemind }) {
  const isOnb = kind === "onboarding";
  const name      = isOnb ? item.candidate_name : item.employee?.full_name;
  const designation = isOnb ? item.designation : item.employee?.designation;
  const dept      = item.department || item.employee?.department || "—";
  const loc       = item.location || item.employee?.location || "Remote";
  const dateLabel = isOnb ? "Joining" : "Last Working Day";
  const date      = isOnb ? item.joining_date : item.last_working_day;
  const checklist = isOnb ? (item.checklist || []) : (item.exit_checklist || []);
  const total     = checklist.length || 1;
  const done      = checklist.filter((c) => c.done).length;
  const pct       = item.progress ?? Math.round((done / total) * 100);
  const stage     = item.stage || (pct >= 100 ? "completed" : pct > 0 ? "in_progress" : "pending");
  const dt        = date ? new Date(date) : null;
  const today     = new Date();
  const days      = dt ? Math.ceil((dt - today) / (1000 * 60 * 60 * 24)) : 0;
  const dayLabel  = !dt ? "—" : isOnb
    ? (days > 0 ? `Starts in ${days}d` : days === 0 ? "Starts today" : `Started ${Math.abs(days)}d ago`)
    : (days > 0 ? `Exit in ${days}d` : days === 0 ? "Exit today" : `Exited ${Math.abs(days)}d ago`);
  const isUrgent = isOnb && days >= 0 && days <= 3;

  return (
    <Card className="p-5 border-slate-200 shadow-sm bg-white hover:shadow-md transition-all min-w-0" data-testid={`journey-${item.id}`}>
      {/* header row */}
      <div className="flex items-start gap-3 mb-3">
        <Avatar className="w-12 h-12 ring-2 ring-white shadow-sm shrink-0">
          {item.avatar_url && <AvatarImage src={item.avatar_url} alt={name} />}
          <AvatarFallback className={`text-white font-bold text-sm ${isOnb ? "bg-gradient-to-br from-violet-500 to-fuchsia-500" : "bg-gradient-to-br from-orange-500 to-rose-500"}`}>
            {initials(name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold font-[Outfit] text-slate-900 truncate">{name}</h3>
            <StatusBadge status={stage} />
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5 truncate">{designation || "—"}</div>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
            <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{dept}</span>
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{loc}</span>
            <span className={`inline-flex items-center gap-1 font-semibold ${isUrgent ? "text-rose-600" : "text-slate-700"}`}>
              <Calendar className="w-3 h-3" />{dayLabel}
            </span>
          </div>
        </div>
        <button className="shrink-0 w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-700">
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>

      {/* progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="font-semibold text-slate-700">Overall Progress</span>
          <span className="font-bold text-slate-900 tabular-nums">{pct}% · {done}/{total}</span>
        </div>
        <Progress value={pct} className="h-2 bg-slate-100" />
      </div>

      {/* checklist preview (first 3) */}
      <div className="space-y-1.5 mb-4">
        {checklist.slice(0, 3).map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-[12px]">
            <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ${
              c.done ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"
            }`}>{c.done ? <CheckCircle2 className="w-2.5 h-2.5" /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}</span>
            <span className={`truncate ${c.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{c.task}</span>
          </div>
        ))}
        {checklist.length > 3 && <div className="text-[11px] text-slate-500 pl-5">+{checklist.length - 3} more tasks</div>}
      </div>

      {/* actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        <Button size="sm" variant="outline" className="border-slate-200 flex-1" onClick={() => onView?.(item)} data-testid={`journey-view-${item.id}`}>
          View Journey
        </Button>
        {pct < 100 && (
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white flex-1" onClick={() => onContinue?.(item)} data-testid={`journey-continue-${item.id}`}>
            Continue <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        )}
        {isUrgent && pct < 100 && (
          <Button size="sm" variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50 shrink-0" onClick={() => onRemind?.(item)}>
            <Bell className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </Card>
  );
}

/* ============== MAIN PAGE ============== */
export default function Lifecycle({ initialTab = "onboarding" }) {
  const [tab, setTab] = useState(initialTab);
  const [onb, setOnb] = useState([]);
  const [off, setOff] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [drawer, setDrawer] = useState({ open: false, kind: null, item: null });

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/onboarding").catch(() => ({ data: [] })),
        api.get("/offboarding").catch(() => ({ data: [] })),
      ]);
      setOnb(a.data || []); setOff(b.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const toggle = async (kind, id, idx) => {
    try {
      await api.patch(`/${kind}/${id}/checklist`, null, { params: { idx } });
      load();
    } catch { toast.error("Failed to update task"); }
  };

  const filterFn = (item, kind) => {
    const isOnb = kind === "onboarding";
    const name = (isOnb ? item.candidate_name : item.employee?.full_name) || "";
    const dept = item.department || item.employee?.department || "";
    if (search && !`${name} ${dept}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && item.stage !== statusFilter) return false;
    if (deptFilter && (item.department || item.employee?.department) !== deptFilter) return false;
    return true;
  };

  const onbFiltered = onb.filter((c) => filterFn(c, "onboarding"));
  const offFiltered = off.filter((c) => filterFn(c, "offboarding"));

  // KPI numbers
  const docsPending  = useMemo(() => onb.reduce((a, c) => a + (c.checklist || []).filter(i => !i.done && /doc/i.test(i.task)).length, 0), [onb]);
  const assetsRecov  = useMemo(() => off.reduce((a, c) => a + (c.exit_checklist || []).filter(i => !i.done && /asset|laptop|device/i.test(i.task)).length, 0), [off]);
  const thisWeekJoin = useMemo(() => {
    const today = new Date(); const week = new Date(today.getTime() + 7 * 86400000);
    return onb.filter(c => { const d = new Date(c.joining_date); return d >= today && d <= week; }).length;
  }, [onb]);

  // Right rail data
  const upcomingStarts = onb.filter(c => new Date(c.joining_date) >= new Date()).slice(0, 4);
  const upcomingExits  = off.filter(c => new Date(c.last_working_day) >= new Date()).slice(0, 4);
  const departments = Array.from(new Set([...onb, ...off].map(c => c.department || c.employee?.department).filter(Boolean)));

  const list = tab === "onboarding" ? onbFiltered : offFiltered;
  const drawerItem = drawer.item;
  const drawerChecklist = drawer.kind === "onboarding" ? (drawerItem?.checklist || []) : (drawerItem?.exit_checklist || []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="lifecycle-page">
      <PageHeader
        category="LIFECYCLE"
        title="Onboarding & Offboarding"
        description="Manage employee journeys from day one to exit — track checklists, documents, assets, and milestones."
        primaryAction={{ label: "Create Journey", icon: Plus, onClick: () => toast.info("Create Journey form (UI ready, wires next sprint)"), testid: "lifecycle-create" }}
        secondaryAction={{ label: "Add Employee", icon: UserPlus, onClick: () => toast.info("Add Employee"), testid: "lifecycle-add" }}
        moreActions={[
          { label: "Bulk Reminders",     icon: Bell,         onClick: () => toast.info("Bulk reminders") },
          { label: "Import Joiners CSV", icon: FileText,     onClick: () => toast.info("Import CSV") },
          { label: "Export Journeys",    icon: ClipboardList,onClick: () => toast.info("Export") },
        ]}
      />

      {/* KPIs */}
      <KPIGrid cols={5}>
        <KPICard icon={UserPlus}  tone="violet"  label="Active Onboarding"  value={onb.filter(c => (c.progress ?? 0) < 100).length} sub="In progress now"     testid="lc-kpi-onb" />
        <KPICard icon={UserMinus} tone="rose"    label="Active Offboarding" value={off.filter(c => (c.progress ?? 0) < 100).length} sub="Exits in motion"    testid="lc-kpi-off" />
        <KPICard icon={FileText}  tone="amber"   label="Documents Pending"  value={docsPending}                                     sub="Across all journeys" testid="lc-kpi-docs" />
        <KPICard icon={Package}   tone="blue"    label="Assets to Recover"  value={assetsRecov}                                     sub="From exits"         testid="lc-kpi-assets" />
        <KPICard icon={Calendar}  tone="emerald" label="This Week Joiners"  value={thisWeekJoin}                                    sub="Next 7 days"        testid="lc-kpi-joiners" />
      </KPIGrid>

      {/* Tabs + Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <PillTabs
          value={tab} onChange={setTab}
          tabs={[
            { value: "onboarding",  label: "Onboarding",  icon: UserPlus,  badge: onb.length },
            { value: "offboarding", label: "Offboarding", icon: UserMinus, badge: off.length },
          ]}
          testid="lifecycle-tabs"
        />
      </div>

      <FilterBar
        search={search} onSearch={setSearch}
        searchPlaceholder="Search by name, designation, department…"
        filters={[
          { label: "Status",     value: statusFilter, onChange: setStatusFilter, options: ["pending", "in_progress", "completed"] },
          { label: "Department", value: deptFilter,   onChange: setDeptFilter,   options: departments },
          { label: "Location",   onChange: () => {}, options: ["Bengaluru","Mumbai","Remote"] },
        ]}
      />

      {/* Main content + right rail */}
      <div className="grid xl:grid-cols-[1fr_320px] gap-5 min-w-0">
        <div className="min-w-0">
          {list.length === 0 ? (
            <EmptyState
              icon={tab === "onboarding" ? UserPlus : UserMinus}
              title={`No ${tab === "onboarding" ? "onboarding" : "offboarding"} journeys yet`}
              message="Create a new journey to start tracking checklists, documents, and milestones for this employee."
              action={{ label: "Create Journey", icon: Plus, onClick: () => toast.info("Create Journey form") }}
              testid="lifecycle-empty"
            />
          ) : (
            <div className="grid md:grid-cols-2 gap-4 min-w-0">
              {list.map((item) => (
                <JourneyCard
                  key={item.id} kind={tab} item={item}
                  onView={(it) => setDrawer({ open: true, kind: tab, item: it })}
                  onContinue={(it) => setDrawer({ open: true, kind: tab, item: it })}
                  onRemind={() => toast.success("Reminder sent")}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right rail */}
        <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
          <SectionCard icon={Calendar} title="Upcoming Joiners" sub="Next 7 days" testid="lc-rail-joiners">
            {upcomingStarts.length === 0 ? (
              <div className="text-[12px] text-slate-400 text-center py-6">No upcoming starts</div>
            ) : (
              <div className="space-y-3">
                {upcomingStarts.map((c) => (
                  <div key={c.id} className="flex items-center gap-2.5">
                    <Avatar className="w-9 h-9 ring-2 ring-white">
                      {c.avatar_url && <AvatarImage src={c.avatar_url} alt={c.candidate_name} />}
                      <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(c.candidate_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{c.candidate_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.designation}</div>
                    </div>
                    <span className="text-[10px] font-bold text-violet-700 bg-violet-50 px-2 py-1 rounded-full">{new Date(c.joining_date).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={UserMinus} title="Upcoming Exits" sub="Plan handoffs">
            {upcomingExits.length === 0 ? (
              <div className="text-[12px] text-slate-400 text-center py-6">No upcoming exits</div>
            ) : (
              <div className="space-y-3">
                {upcomingExits.map((c) => (
                  <div key={c.id} className="flex items-center gap-2.5">
                    <Avatar className="w-9 h-9 ring-2 ring-white">
                      {c.employee?.avatar_url && <AvatarImage src={c.employee.avatar_url} alt={c.employee.full_name} />}
                      <AvatarFallback className="bg-gradient-to-br from-orange-500 to-rose-500 text-white text-[10px] font-bold">{initials(c.employee?.full_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{c.employee?.full_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.employee?.designation}</div>
                    </div>
                    <span className="text-[10px] font-bold text-rose-700 bg-rose-50 px-2 py-1 rounded-full">{new Date(c.last_working_day).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={AlertCircle} title="Pending Tasks" sub="Items awaiting action">
            <div className="space-y-2.5">
              {[
                { label: "Documents to verify",  count: docsPending,  tone: "amber" },
                { label: "Assets to recover",    count: assetsRecov,  tone: "blue"  },
                { label: "Reminders to send",    count: 3,            tone: "violet" },
              ].map((t) => {
                const ring = { amber: "bg-amber-50 text-amber-700", blue: "bg-blue-50 text-blue-700", violet: "bg-violet-50 text-violet-700" }[t.tone];
                return (
                  <div key={t.label} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                    <span className="text-[12px] font-semibold text-slate-700">{t.label}</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${ring}`}>{t.count}</span>
                  </div>
                );
              })}
            </div>
            <button className="mt-3 text-[12px] font-semibold text-violet-700 hover:underline inline-flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </SectionCard>
        </aside>
      </div>

      {/* DETAIL DRAWER */}
      <Sheet open={drawer.open} onOpenChange={(o) => setDrawer({ ...drawer, open: o })}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="lifecycle-drawer">
          <SheetHeader>
            <SheetTitle className="font-[Outfit] text-xl">
              {drawer.kind === "onboarding" ? drawerItem?.candidate_name : drawerItem?.employee?.full_name}
            </SheetTitle>
            <p className="text-sm text-slate-500">{drawer.kind === "onboarding" ? drawerItem?.designation : drawerItem?.employee?.designation}</p>
          </SheetHeader>
          {drawerItem && (
            <div className="mt-5 space-y-5">
              <Card className="p-4 border-slate-200 shadow-none bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Progress</div>
                <div className="mt-2 flex items-center gap-3">
                  <Progress value={drawerItem.progress ?? 0} className="h-2 flex-1" />
                  <span className="text-sm font-bold text-slate-900 tabular-nums">{drawerItem.progress ?? 0}%</span>
                </div>
              </Card>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Checklist</div>
                <div className="space-y-1.5">
                  {drawerChecklist.map((item, i) => (
                    <label key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`drawer-item-${i}`}>
                      <Checkbox checked={item.done} onCheckedChange={() => toggle(drawer.kind, drawerItem.id, i)} />
                      <span className={`text-sm flex-1 ${item.done ? "line-through text-slate-400" : "text-slate-700"}`}>{item.task}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
